import React, { useState, useEffect } from 'react';
import { Input, Tooltip, Upload, message } from 'antd';
import { 
  SearchOutlined, 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  MoreOutlined,
  UploadOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { useResponsive } from '../../../../hooks/useResponsive';
import { CreateButton, DeleteButton, DownloadButton } from '../../../common/buttons';
import * as S from './StudentsTable.styles';

export interface Student {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
  grade?: number;
  lastMark?: number;
  lastActivity?: string;
}

export interface StudentsTableProps {
  students: Student[];
  onAddStudent: () => void;
  onEditStudent: (student: Student) => void;
  onDeleteStudent: (studentId: number) => void;
  onStudentClick?: (studentId: number) => void;
  onImportCSV?: () => void;
  loading?: boolean;
  className?: string;
}

export const StudentsTable: React.FC<StudentsTableProps> = ({
  students = [],
  onAddStudent,
  onEditStudent,
  onDeleteStudent,
  onStudentClick,
  onImportCSV,
  loading = false,
  className
}) => {
  const { isDesktop, isTablet } = useResponsive();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStudents, setSelectedStudents] = useState<number[]>([]);

  // Filter students based on search query (only by name)
  const filteredStudents = students.filter(student =>
    student.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCSVClick = () => {
    if (onImportCSV) {
      onImportCSV();
    }
  };

  const handleSelectStudent = (studentId: number) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredStudents.map(s => s.id));
    }
  };

  // Desktop table view
  const renderDesktopTable = () => (
    <S.TableContainer>
      <S.TableHeader>
        <S.HeaderActions>
          <S.SearchContainer>
            <Input
              placeholder="Search students..."
              prefix={<SearchOutlined />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: 280 }}
            />
          </S.SearchContainer>
          
          <S.ActionButtons>
            {onImportCSV && (
              <Tooltip title="Import students from CSV">
                <DownloadButton onClick={handleCSVClick}>
                  Import CSV
                </DownloadButton>
              </Tooltip>
            )}
            
            <Tooltip title="Add a new student to this class">
              <CreateButton onClick={onAddStudent}>
                Add Student
              </CreateButton>
            </Tooltip>
          </S.ActionButtons>
        </S.HeaderActions>
      </S.TableHeader>

      <S.Table>
        <S.TableHead>
          <S.TableRow>
            <S.TableHeaderCell>
              <S.Checkbox 
                checked={selectedStudents.length === filteredStudents.length && filteredStudents.length > 0}
                onChange={handleSelectAll}
              />
            </S.TableHeaderCell>
            <S.TableHeaderCell>Name</S.TableHeaderCell>
            <S.TableHeaderCell>Phone</S.TableHeaderCell>
            <S.TableHeaderCell>E-mail</S.TableHeaderCell>
            <S.TableHeaderCell>Date of Birth</S.TableHeaderCell>
            <S.TableHeaderCell>Avg Grade</S.TableHeaderCell>
            <S.TableHeaderCell>Last Mark</S.TableHeaderCell>
            <S.TableHeaderCell>Actions</S.TableHeaderCell>
          </S.TableRow>
        </S.TableHead>
        
        <S.TableBody>
          {filteredStudents.map((student) => (
            <S.TableRow key={student.id}>
              <S.TableCell>
                <S.Checkbox 
                  checked={selectedStudents.includes(student.id)}
                  onChange={() => handleSelectStudent(student.id)}
                />
              </S.TableCell>
              
              <S.TableCell>
                <S.StudentInfo>
                  <S.StudentAvatar 
                    onClick={() => onStudentClick && onStudentClick(student.id)}
                    style={{ cursor: onStudentClick ? 'pointer' : 'default' }}
                  >
                    <UserOutlined />
                  </S.StudentAvatar>
                  <S.StudentDetails>
                    <S.StudentName>{student.name}</S.StudentName>
                  </S.StudentDetails>
                </S.StudentInfo>
              </S.TableCell>
              
              <S.TableCell>
                {student.phone ? (
                  <S.ContactItem>
                    <PhoneOutlined />
                    {student.phone}
                  </S.ContactItem>
                ) : (
                  <span style={{ color: 'rgba(0, 0, 0, 0.25)' }}>—</span>
                )}
              </S.TableCell>
              
              <S.TableCell>
                {student.email ? (
                  <S.ContactItem>
                    <MailOutlined />
                    {student.email}
                  </S.ContactItem>
                ) : (
                  <span style={{ color: 'rgba(0, 0, 0, 0.25)' }}>—</span>
                )}
              </S.TableCell>
              
              <S.TableCell>
                {student.dateOfBirth ? (
                  <span style={{ fontSize: '13px', color: 'rgba(0, 0, 0, 0.65)' }}>
                    {new Date(student.dateOfBirth).toLocaleDateString()}
                  </span>
                ) : (
                  <span style={{ color: 'rgba(0, 0, 0, 0.25)' }}>—</span>
                )}
              </S.TableCell>
              
              <S.TableCell>
                <S.GradeBadge grade={student.grade || 0}>
                  {student.grade ? `${student.grade}/10` : 'N/A'}
                </S.GradeBadge>
              </S.TableCell>
              
              <S.TableCell>
                <S.GradeBadge grade={student.lastMark || 0}>
                  {student.lastMark ? `${student.lastMark}/10` : 'N/A'}
                </S.GradeBadge>
              </S.TableCell>
              
              <S.TableCell>
                <S.ActionsContainer>
                  <Tooltip title="Edit student">
                    <S.ActionButton onClick={() => onEditStudent(student)}>
                      <EditOutlined />
                    </S.ActionButton>
                  </Tooltip>
                  <Tooltip title="Delete student">
                    <S.ActionButton 
                      danger 
                      onClick={() => onDeleteStudent(student.id)}
                    >
                      <DeleteOutlined />
                    </S.ActionButton>
                  </Tooltip>
                </S.ActionsContainer>
              </S.TableCell>
            </S.TableRow>
          ))}
        </S.TableBody>
      </S.Table>

      {filteredStudents.length === 0 && (
        <S.EmptyState>
          <UserOutlined />
          <h3>No students found</h3>
          <p>
            {searchQuery 
              ? `No students match "${searchQuery}"`
              : 'This class has no students yet. Use the "Add Student" button above to add your first student.'
            }
          </p>
        </S.EmptyState>
      )}
    </S.TableContainer>
  );

  // Mobile cards view
  const renderMobileCards = () => (
    <S.MobileContainer>
      <S.MobileHeader>
        <S.SearchContainer>
          <Input
            placeholder="Search students..."
            prefix={<SearchOutlined />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </S.SearchContainer>
        
        <S.MobileActions>
          {onImportCSV && (
            <S.MobileActionButton onClick={handleCSVClick}>
              <UploadOutlined />
            </S.MobileActionButton>
          )}
          
          <S.MobileActionButton onClick={onAddStudent}>
            <PlusOutlined />
          </S.MobileActionButton>
        </S.MobileActions>
      </S.MobileHeader>

      <S.CardsContainer>
        {filteredStudents.map((student) => (
          <S.StudentCard key={student.id}>
            <S.CardHeader>
              <S.StudentInfo>
                <S.StudentAvatar 
                  onClick={() => onStudentClick && onStudentClick(student.id)}
                  style={{ cursor: onStudentClick ? 'pointer' : 'default' }}
                >
                  <UserOutlined />
                </S.StudentAvatar>
                <S.StudentDetails>
                  <S.StudentName>{student.name}</S.StudentName>
                </S.StudentDetails>
              </S.StudentInfo>
              
              <S.CardActions>
                <S.ActionButton onClick={() => onEditStudent(student)}>
                  <EditOutlined />
                </S.ActionButton>
                <S.ActionButton 
                  danger 
                  onClick={() => onDeleteStudent(student.id)}
                >
                  <DeleteOutlined />
                </S.ActionButton>
              </S.CardActions>
            </S.CardHeader>
            
            <S.CardContent>
              <S.ContactItem>
                <MailOutlined />
                {student.email || <S.EmptyValue>—</S.EmptyValue>}
              </S.ContactItem>
              
              <S.ContactItem>
                <PhoneOutlined />
                {student.phone || <S.EmptyValue>—</S.EmptyValue>}
              </S.ContactItem>
              
              <S.StatsRow>
                <S.StatItem>
                  <S.StatLabel>Grade</S.StatLabel>
                  <S.GradeBadge grade={student.grade || 0}>
                    {student.grade ? `${student.grade}/10` : 'N/A'}
                  </S.GradeBadge>
                </S.StatItem>
                
                <S.StatItem>
                  <S.StatLabel>Last Mark</S.StatLabel>
                  <S.GradeBadge grade={student.lastMark || 0}>
                    {student.lastMark ? `${student.lastMark}/10` : 'N/A'}
                  </S.GradeBadge>
                </S.StatItem>
              </S.StatsRow>
              
              <S.LastActivityRow>
                <CalendarOutlined />
                Date of Birth: {student.dateOfBirth ? new Date(student.dateOfBirth).toLocaleDateString() : <S.EmptyValue>—</S.EmptyValue>}
              </S.LastActivityRow>
            </S.CardContent>
          </S.StudentCard>
        ))}
      </S.CardsContainer>

      {filteredStudents.length === 0 && (
        <S.EmptyState>
          <UserOutlined />
          <h3>No students found</h3>
          <p>
            {searchQuery 
              ? `No students match "${searchQuery}"`
              : 'This class has no students yet. Use the "Add Student" button above to add your first student.'
            }
          </p>
        </S.EmptyState>
      )}
    </S.MobileContainer>
  );

  return (
    <S.Container className={className}>
      {isDesktop ? renderDesktopTable() : renderMobileCards()}
    </S.Container>
  );
};
